const NAVIGATION_STATE_CACHE_KEY = "familysafe.navigation-state-cache";
const PERSISTED_ROUTE_STATE_CACHE_KEY_PREFIX = "familysafe.route-state.";

interface CachedNavigationState {
  returnTo: string;
  routeState: unknown;
}

export const cacheNavigationState = (returnTo: string, routeState: unknown): void => {
  if (typeof window === "undefined" || !returnTo) {
    return;
  }

  try {
    const payload: CachedNavigationState = {
      returnTo,
      routeState,
    };

    window.sessionStorage.setItem(NAVIGATION_STATE_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to cache navigation state for unlock recovery:", error);
  }
};

export const consumeCachedNavigationState = (returnTo: string): unknown | null => {
  if (typeof window === "undefined" || !returnTo) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(NAVIGATION_STATE_CACHE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as CachedNavigationState | null;
    window.sessionStorage.removeItem(NAVIGATION_STATE_CACHE_KEY);

    if (!parsed || parsed.returnTo !== returnTo) {
      return null;
    }

    return parsed.routeState ?? null;
  } catch (error) {
    window.sessionStorage.removeItem(NAVIGATION_STATE_CACHE_KEY);
    console.warn("Failed to restore cached navigation state after unlock:", error);
    return null;
  }
};

const getPersistedRouteStateStorageKey = (routeKey: string): string =>
  `${PERSISTED_ROUTE_STATE_CACHE_KEY_PREFIX}${encodeURIComponent(routeKey)}`;

export const persistRouteState = (routeKey: string, routeState: unknown): void => {
  if (typeof window === "undefined" || !routeKey || routeState == null) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      getPersistedRouteStateStorageKey(routeKey),
      JSON.stringify(routeState)
    );
  } catch (error) {
    console.warn("Failed to persist route state for reload recovery:", error);
  }
};

export const getPersistedRouteState = (routeKey: string): unknown | null => {
  if (typeof window === "undefined" || !routeKey) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(getPersistedRouteStateStorageKey(routeKey));
    if (!rawValue) {
      return null;
    }

    return JSON.parse(rawValue);
  } catch (error) {
    console.warn("Failed to read persisted route state for reload recovery:", error);
    return null;
  }
};

export const clearNavigationStateCache = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(NAVIGATION_STATE_CACHE_KEY);

    const keysToRemove: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PERSISTED_ROUTE_STATE_CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (error) {
    console.warn("Failed to clear cached navigation state:", error);
  }
};
